import { createFieldConfig } from '../model/fieldConfig';
import { parseScript }       from '../scriptParser';

const CUSTOMER_NAME_FIELD_GROUP = 'Customer Name';
const CUSTOMER_NAME_CHARS_REGEX = "^[A-Za-z0-9~@#$%()_{}:;'/[\\]&,.-]*$";
const CUSTOMER_NAME_CHARS_MESSAGE = "Only letters, numbers, and special characters including ~@#$%()_{}:;'/[],&.- are allowed";

export function normalizeAll(dbRows) {
  if (!Array.isArray(dbRows)) return [];

  const allFieldKeys = dbRows.map(row => adaptFieldKey(row));

  const configs = dbRows
    .map(row => normalizeFieldConfig(row, allFieldKeys))
    .filter(config => !(config.group === 'DateTimeTimezone' && config.fieldType === 'timezone'))
    .sort((a, b) => a.sequence - b.sequence);

  relocateEventDrivenOptionFilters(configs);

  return configs;
}

function relocateEventDrivenOptionFilters(configs) {
  const dispatcherByEvent = new Map();
  configs.forEach(c => {
    (c.__dispatchedEvents || []).forEach(evtName => {
      if (!dispatcherByEvent.has(evtName)) {
        dispatcherByEvent.set(evtName, c.fieldKey);
      }
    });
  });

  configs.forEach(c => {
    const of = c.optionFilter;
    if (!of || !of.driverEvent) return;
    const driverKey = dispatcherByEvent.get(of.driverEvent);
    if (!driverKey || driverKey === c.fieldKey) return;

    const driver = configs.find(x => x.fieldKey === driverKey);
    if (!driver) return;

    const { driverEvent, ...clean } = of;

    if (!driver.optionFilter) {
      driver.optionFilter = clean;
    } else {
      if (!Array.isArray(driver.optionFilters)) driver.optionFilters = [];
      driver.optionFilters.push(clean);
    }
    c.optionFilter = null;
  });

  configs.forEach(c => { delete c.__dispatchedEvents; });
}

export function normalizeFieldConfig(dbRow, allFieldKeys = []) {
  const fieldKey  = adaptFieldKey(dbRow);
  const fieldType = extractFieldType(fieldKey, dbRow);
  const name      = extractName(fieldKey, dbRow);

  const group = adaptGroup(dbRow);
  const fieldGroup = adaptFieldGroup(dbRow);

  const initScript = adaptScript(dbRow, 'pre_init');
  const saveScript = adaptScript(dbRow, 'pre_save');

  const initResult = initScript
    ? parseScript(initScript, fieldKey, allFieldKeys, 'pre_init')
    : { effects: [], optionFilter: null, validation: [], dispatchedEvents: [] };

  const saveResult = saveScript
    ? parseScript(saveScript, fieldKey, allFieldKeys, 'pre_save')
    : { validation: [] };

  const isRequired = adaptIsRequired(dbRow);
  const maxLength = adaptMaxLength(dbRow) || initResult.maxLength || 0;

  const validation = [
    ...(Array.isArray(dbRow.validation) ? dbRow.validation : []),
    ...(initResult.validation ?? []),
    ...(saveResult.validation ?? []),
  ];

  if (isRequired && !validation.some(v => v.rule === 'required')) {
    validation.unshift({ rule: 'required' });
  }

  if (maxLength && (fieldType === 'string' || fieldType === 'text')
      && !validation.some(v => v.rule === 'maxLength')) {
    validation.push({ rule: 'maxLength', value: maxLength });
  }

  if (fieldGroup === CUSTOMER_NAME_FIELD_GROUP && (fieldType === 'string' || fieldType === 'text')
      && !validation.some(v => v.rule === 'regex')) {
    validation.push({ rule: 'regex', value: CUSTOMER_NAME_CHARS_REGEX, message: CUSTOMER_NAME_CHARS_MESSAGE });
  }

  const resolvedFieldType = (group === 'DateTimeTimezone' && fieldType === 'datetime')
    ? 'date'
    : fieldType;

  const base = createFieldConfig({
    fieldKey,
    fieldType: resolvedFieldType,
    label:         adaptLabel(dbRow),
    name,
    sequence:      adaptSequence(dbRow),
    group,
    fieldGroup,
    isRequired,
    maxLength,
    isNonEditable: adaptIsNonEditable(dbRow),
    isSystemField: adaptIsSystemField(dbRow),
    defaultValue:  adaptDefaultValue(dbRow, fieldType),
    options:       normalizeOptions(adaptOptions(dbRow)),
    effects:       initResult.effects ?? [],
    validation,
    optionFilter:  initResult.optionFilter ?? null,
    fieldSettings: adaptFieldSettings(dbRow),
    description:   dbRow.description ?? '',
  });

  base.__dispatchedEvents = initResult.dispatchedEvents ?? [];

  if (base.defaultValue === '-1' && base.options.length > 0
      && !base.options.some(o => o.value === '-1')) {
    base.defaultValue = base.options[0].value;
  }

  return base;
}

export function normalizeOptions(rawOptions) {
  if (!Array.isArray(rawOptions) || rawOptions.length === 0) return [];

  return rawOptions
    .filter(opt => opt != null)
    .map(opt => {
      if (opt.key !== undefined) {
        return { value: String(opt.key), label: opt.value ?? opt.key };
      }
      if (opt.Value !== undefined) {
        return { value: String(opt.Value), label: opt.Text ?? opt.Value };
      }
      if (opt.id !== undefined) {
        return { value: String(opt.id), label: opt.name ?? opt.id };
      }
      if (opt.value !== undefined) {
        return { value: String(opt.value), label: opt.label ?? opt.value };
      }
      if (typeof opt === 'string') {
        return { value: opt, label: opt };
      }
      return null;
    })
    .filter(Boolean);
}

function adaptFieldKey(row) {
  return row.cf_field_name ?? row.fieldName ?? row.Name ?? row.FieldKey ?? row.fieldKey ?? '';
}

function adaptLabel(row) {
  const explicit = row.cf_label ?? row.label ?? row.Label ?? null;
  if (explicit !== null && explicit !== '') return String(explicit);
  const fromGroup = row.fieldGroupName ?? row.field_group_name ?? row.FieldGroup ?? row.LabelName ?? null;
  if (fromGroup && fromGroup !== '') return String(fromGroup);
  const humanName = row.FieldName ?? null;
  if (humanName && humanName !== '') return String(humanName);
  return formatLabelFromKey(adaptFieldKey(row));
}

function adaptSequence(row) {
  return Number(row.sequence ?? row.Sequence ?? 0);
}

function adaptGroup(row) {
  return row.cf_group ?? row.group ?? row.Group ?? '';
}

function adaptFieldGroup(row) {
  return row.cf_field_group ?? row.fieldGroupName ?? row.field_group_name ?? row.FieldGroup ?? row.LabelName ?? '';
}

function adaptIsRequired(row) {
  const val = row.is_required ?? row.isRequired ?? row.IsRequired ?? 0;
  return val === 1 || val === true;
}

function adaptMaxLength(row) {
  const val = Number(row.max_length ?? row.maxLength ?? row.MaxLength ?? 0);
  return Number.isFinite(val) && val > 0 ? val : null;
}

function adaptIsNonEditable(row) {
  if('is_editable' in row || 'isEditable' in row || 'IsEditable' in row) {
    const editableVal = row.is_editable ?? row.isEditable ?? row.IsEditable ?? 1
    const isEditable = editableVal === 1 || editableVal === true
    return !isEditable
  }
  const val = row.is_non_editable ?? row.isNonEditable ?? row.IsNonEditable ?? 0;
  return val === 1 || val === true;
}

function adaptIsSystemField(row) {
  const val = row.is_system_field ?? row.isSystemField ?? row.IsSystemField ?? false;
  return val === 1 || val === true;
}

function adaptDefaultValue(row, fieldType) {
  const raw = row.default_value ?? row.DefaultValue ?? row.Value ?? row.value ?? '';
  if ((fieldType === 'singlechoicelist' || fieldType === 'multiplechoicelist') && !raw) {
    return '-1';
  }
  if (fieldType === 'country' && !raw) {
    return 'US';
  }
  return raw ?? '';
}

function adaptOptions(row) {
  return row.CustomFieldValues ?? row.custom_field_values ?? row.items ?? null;
}

function adaptFieldSettings(row) {
  const raw = row.fieldSettings ?? row.FieldSettings ?? row.field_settings ?? row.settings ?? null;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function adaptScript(row, type) {
  if (type === 'pre_init') {
    return row.pre_init_script ?? row.preInitScript ?? row.PreInitScript ?? null;
  }
  if (type === 'pre_save') {
    return row.pre_save_script
      ?? row.preSaveScript
      ?? row.presaveScript
      ?? row.PreSaveScript
      ?? null;
  }
  return null;
}

function extractFieldType(fieldKey, dbRow) {
  if (dbRow.FieldType) return dbRow.FieldType.toLowerCase();
  if (dbRow.fieldType) return dbRow.fieldType.toLowerCase();

  const parts = fieldKey.split('_');
  if (parts.length >= 3) {
    const type = parts[parts.length - 2].toLowerCase();
    if (KNOWN_FIELD_TYPES.has(type)) return type;
  }

  return 'string';
}

function extractName(fieldKey, dbRow) {
  if (dbRow.Name && !dbRow.Name.includes('_')) return dbRow.Name;

  const parts = fieldKey.split('_');
  if (parts.length >= 3) {
    return parts.slice(0, parts.length - 2).join('_');
  }
  return fieldKey;
}


function formatLabelFromKey(key) {
  const STRIP_TYPES = new Set([
    'string','text','number','decimal','email','date','datetime',
    'datetimetimezone','datewithusertimezone','singlechoicelist',
    'multiplechoicelist','address','name','timezone','file','geopoint',
    'zip','city','state','county','country','richtext','richtexteditor',
    'checkbox','static',
  ]);
  const parts = key.split('_');
  const nameParts = parts.filter(p => p && !STRIP_TYPES.has(p.toLowerCase()) && !/^\d+$/.test(p));
  const base = (nameParts.length > 0 ? nameParts : parts).join(' ');
  return base
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

const KNOWN_FIELD_TYPES = new Set([
  'string', 'text', 'number', 'decimal', 'email', 'date', 'datetime',
  'datetimetimezone', 'datewithusertimezone', 'singlechoicelist',
  'multiplechoicelist', 'address', 'name', 'timezone', 'file',
  'geopoint', 'zip', 'city', 'state', 'county', 'country',
  'richtext', 'richtexteditor',
]);
